import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { ToastContainer, toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "react-select";
import "react-toastify/dist/ReactToastify.css";
import "../campus/Campus.css";
import { hasAdmissionPermission } from "hooks/permissions";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

const primaryButtonStyle = {
  color: "white",
  backgroundColor: "#28166f",
};

const campaignSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "40px",
    fontSize: "14px",
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

const ApplicationDrafts = () => {
  // State variables
  const [drafts, setDrafts] = useState([]);
  const [allDrafts, setAllDrafts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [applicationFormId, setApplicationFormId] = useState("");
  const [draftNo, setDraftNo] = useState("");
  const [draftDate, setDraftDate] = useState("");
  const [amount, setAmount] = useState("");
  const [showAddDraftModal, setShowAddDraftModal] = useState(false);
  const [showUpdateDraftModal, setShowUpdateDraftModal] = useState(false);
  const [showDeleteDraftModal, setShowDeleteDraftModal] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [showCancelDraftModal, setShowCancelDraftModal] = useState(false);
  const [cancelDraftId, setCancelDraftId] = useState(null);

  const [formErrors, setFormErrors] = useState({
    application_form_id: "",
    draft_no: "",
    draft_date: "",
    amount: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const getCampaigns = async () => {
    try {
      const response = await fetch(`${BASE_URL}/campaign/get_campaigns`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        setCampaigns(data.campaigns || []);
      } else {
        toast.error("Failed to fetch campaigns");
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      toast.error("Failed to fetch campaigns");
    }
  };

  // Fetch drafts for selected campaign (only when user clicks Fetch)
  const getDrafts = async () => {
    if (!selectedCampaign?.value) {
      toast.error("Please select a campaign first");
      return;
    }

    setIsLoading(true);
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(
        `${BASE_URL}/application/get_all_drafts?campaign_id=${selectedCampaign.value}`,
        requestOptions
      );
      const data = await response.json();

      if (response.ok) {
        const list = data.drafts || data || [];
        setAllDrafts(list);
        setDrafts(list);
        setSearchKeyword("");
        setHasFetched(true);
      } else {
        toast.error(data.error || "Failed to fetch drafts");
        setAllDrafts([]);
        setDrafts([]);
      }
    } catch (error) {
      console.error("Error fetching drafts:", error);
      toast.error("Failed to fetch drafts");
      setAllDrafts([]);
      setDrafts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Add draft
  const addDraft = async () => {
    const data = {
      application_form_id: parseInt(applicationFormId),
      draft_no: draftNo,
      draft_date: draftDate,
      amount: parseFloat(amount)
    };

    try {
      const response = await fetch(`${BASE_URL}/application/add_draft`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error("Failed to add draft");
      }

      toast.success("Draft added successfully");
      getDrafts();
      resetForm();
    } catch (error) {
      console.error("Error adding draft:", error);
      toast.error("Failed to add draft");
    } finally {
      setShowAddDraftModal(false);
    }
  };

  // Update draft
  const updateDraft = async () => {
    const data = {
      id: currentDraftId,
      application_form_id: parseInt(applicationFormId),
      draft_no: draftNo,
      draft_date: draftDate,
      amount: parseFloat(amount)
    };

    try {
      const response = await fetch(`${BASE_URL}/application/update_draft`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.message || "Failed to update draft");
      }

      toast.success("Draft updated successfully");
      getDrafts();
      resetForm();
    } catch (error) {
      console.error("Error updating draft:", error);
      toast.error("Failed to update draft");
    } finally {
      setShowUpdateDraftModal(false);
    }
  };

  // Delete draft
  const deleteDraft = async () => {
    try {
      const response = await fetch(`${BASE_URL}/application/delete_draft/${currentDraftId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete draft");
      }

      setDrafts(drafts.filter((draft) => draft.id !== currentDraftId));
      setAllDrafts(allDrafts.filter((draft) => draft.id !== currentDraftId));
      toast.success("Draft deleted successfully");
    } catch (error) {
      console.error("Error deleting draft:", error);
      toast.error("Failed to delete draft");
    } finally {
      setShowDeleteDraftModal(false);
    }
  };
  const updateDraftStatus = async (draftID) => {
    try {
      const response = await fetch(`${BASE_URL}/application/update_application_draft_status/${draftID}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to update draft status");
      }

      toast.success("Draft status updated successfully");
      getDrafts();
    } catch (error) {
      console.error("Error updating draft status:", error);
      toast.error("Failed to update draft status");
    }
  }

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!applicationFormId || applicationFormId === "") {
      errors.application_form_id = "Application Form ID is required";
    }
    if (!draftNo || draftNo.trim() === "") {
      errors.draft_no = "Draft Number is required";
    }
    if (!draftDate || draftDate.trim() === "") {
      errors.draft_date = "Draft Date is required";
    }
    if (!amount || amount === "" || isNaN(parseFloat(amount))) {
      errors.amount = "Valid amount is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form focus to clear errors
  const handleFocus = (e) => {
    const { name } = e.target;
    setFormErrors((prevFormErrors) => ({
      ...prevFormErrors,
      [name]: "",
    }));
  };

  // Reset form
  const resetForm = () => {
    setApplicationFormId("");
    setDraftNo("");
    setDraftDate("");
    setAmount("");
    setFormErrors({
      application_form_id: "",
      draft_no: "",
      draft_date: "",
      amount: ""
    });
  };

  // Search functionality
  const handleSearchKeywordChange = (e) => {
    const searchTerm = e.target.value.trim().toLowerCase();
    setSearchKeyword(searchTerm);

    if (searchTerm === "") {
      setDrafts(allDrafts);
    } else {
      const filteredDrafts = allDrafts.filter((draft) => {
        return (
          String(draft.draft_no || "").toLowerCase().includes(searchTerm) ||
          String(draft.application_form_id || "").includes(searchTerm) ||
          String(draft.amount || "").includes(searchTerm) ||
          String(draft.draft_date || "").toLowerCase().includes(searchTerm)
        );
      });
      setDrafts(filteredDrafts);
    }
  };

  // Modal handlers
  const showDraftAddModal = () => {
    resetForm();
    setShowAddDraftModal(true);
  };

  const showDraftUpdateModal = (id) => {
    setCurrentDraftId(id);
    const draft = drafts.find((d) => d.id === id);
    if (draft) {
      setApplicationFormId(draft.application_form_id.toString());
      setDraftNo(draft.draft_no);
      setDraftDate(draft.draft_date);
      setAmount(draft.amount.toString());
    }
    setShowUpdateDraftModal(true);
  };

  const showDraftDeleteModal = (id) => {
    setCurrentDraftId(id);
    setShowDeleteDraftModal(true);
  };

  const handleCancelAddDraftModal = () => {
    setShowAddDraftModal(false);
    resetForm();
  };

  const handleOkAddDraftModal = () => {
    if (validateForm()) {
      addDraft();
    }
  };

  const handleOkUpdateDraftModal = () => {
    if (validateForm()) {
      updateDraft();
    }
  };

  // Load campaigns only on mount — drafts load after user clicks Fetch
  useEffect(() => {
    getCampaigns();
  }, []);

  const campaignOptions = campaigns.map((campaign) => ({
    value: campaign.id,
    label: campaign.name,
  }));

  // Table configuration
  const columns = [
    { Header: "Sr.No", accessor: "serial", align: "center" },
    { Header: "ID", accessor: "id", align: "center" },
    { Header: "Application Form ID", accessor: "application_form_id", align: "center" },
    { Header: "Draft No", accessor: "draft_no", align: "center" },
    { Header: "Draft Date", accessor: "draft_date", align: "center" },
    { Header: "Amount", accessor: "amount", align: "center" },
    { Header: "Actions", accessor: "actions", align: "center" },
  ];

  const rows = drafts.map((draft, index) => ({
    serial: index + 1,
    id: draft.id,
    application_form_id: draft.application_form_id,
    draft_no: draft.draft_no,
    draft_date: draft.draft_date,
    amount: `${parseFloat(draft.amount).toFixed(2)} PKR`,
    actions: (
      <>
        {hasAdmissionPermission("ApplicationDrafts", "UPDATE") && (
          <IconButton onClick={() => showDraftUpdateModal(draft.id)}>
            <EditIcon className="icon-button-edit" />
          </IconButton>
        )}
        {hasAdmissionPermission("ApplicationDrafts", "DELETE") && (
          <IconButton onClick={() => showDraftDeleteModal(draft.id)}>
            <DeleteIcon className="icon-button-delete" />
          </IconButton>
        )}
        <Button
          variant="text"
          color={draft.is_cancel ? "error" : "success"}
          disabled={draft.is_cancel === true}
          onClick={() => {
            setCancelDraftId(draft.id);
            setShowCancelDraftModal(true);
          }}
        >
          {draft.is_cancel ? "Canceled" : "Cancel"}
        </Button>
      </>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card className="card1">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "16px",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ minWidth: "280px" }}>
                    <Select
                      options={campaignOptions}
                      value={selectedCampaign}
                      onChange={(option) => {
                        setSelectedCampaign(option);
                        setDrafts([]);
                        setAllDrafts([]);
                        setHasFetched(false);
                        setSearchKeyword("");
                      }}
                      placeholder="Select Campaign"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={campaignSelectStyles}
                    />
                  </div>
                  <Button
                    variant="contained"
                    onClick={getDrafts}
                    disabled={isLoading || !selectedCampaign}
                    style={{
                      ...primaryButtonStyle,
                      minWidth: "110px",
                      height: "40px",
                    }}
                  >
                    {isLoading ? "Fetching..." : "Fetch"}
                  </Button>
                  <Paper className="search-paper" component="form">
                    <InputBase
                      style={{ marginLeft: "8px", flex: 1, fontSize: "16px" }}
                      placeholder="Search drafts..."
                      inputProps={{ "aria-label": "search drafts" }}
                      value={searchKeyword}
                      onChange={handleSearchKeywordChange}
                      disabled={!hasFetched}
                    />
                    <SearchIcon className="search-icon" />
                  </Paper>
                </div>
                {hasAdmissionPermission("ApplicationDrafts", "CREATE") && (
                  <Button
                    className="add-button"
                    variant="contained"
                    endIcon={<AddIcon className="add-icon" />}
                    onClick={showDraftAddModal}
                    style={primaryButtonStyle}
                  >
                    Add
                  </Button>
                )}
              </div>
              <MDBox pt={3}>
                {isLoading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "24px" }}>
                    <CircularProgress />
                  </div>
                ) : !hasFetched ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 16px",
                      color: "#64748b",
                      fontSize: "15px",
                    }}
                  >
                    Select a campaign and click Fetch to load application drafts.
                  </div>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Add Draft Dialog */}
      <Dialog
        open={showAddDraftModal}
        onClose={handleCancelAddDraftModal}
        maxWidth="lg"
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-title">
          Add Application Draft
        </DialogTitle>
        <DialogContent className="dialog-content">
          <div className="row">
            <div className="col-md-6 left-fields">
              <div className="form-outline mt-3">
                <label>Application Form ID</label>
                <input
                  name="application_form_id"
                  type="number"
                  value={applicationFormId}
                  onChange={(e) => setApplicationFormId(e.target.value)}
                  onFocus={handleFocus}
                  className="form-control form-control-lg"
                  placeholder="Enter Application Form ID"
                />
                <div className="error-message1">{formErrors.application_form_id}</div>
              </div>
              <div className="form-outline mt-3">
                <label>Draft Number</label>
                <input
                  name="draft_no"
                  type="text"
                  value={draftNo}
                  onChange={(e) => setDraftNo(e.target.value)}
                  onFocus={handleFocus}
                  className="form-control form-control-lg"
                  placeholder="Enter Draft Number"
                />
                <div className="error-message1">{formErrors.draft_no}</div>
              </div>
            </div>
            <div className="col-md-6 form-sideline">
              <div className="form-outline mt-3">
                <label>Draft Date</label>
                <input
                  name="draft_date"
                  type="date"
                  value={draftDate}
                  onChange={(e) => setDraftDate(e.target.value)}
                  onFocus={handleFocus}
                  className="form-control form-control-lg"
                />
                <div className="error-message">{formErrors.draft_date}</div>
              </div>
              <div className="form-outline mt-3">
                <label>Amount</label>
                <input
                  name="amount"
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  onFocus={handleFocus}
                  className="form-control form-control-lg"
                  placeholder="Enter Amount"
                />
                <div className="error-message">{formErrors.amount}</div>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={handleCancelAddDraftModal}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={handleOkAddDraftModal}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Draft Dialog */}
      <Dialog
        open={showUpdateDraftModal}
        onClose={() => setShowUpdateDraftModal(false)}
        maxWidth="lg"
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-title">
          Update Application Draft
        </DialogTitle>
        <DialogContent className="dialog-content">
          <div className="row">
            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Application Form ID</label>
                <input
                  name="application_form_id"
                  type="number"
                  value={applicationFormId}
                  onChange={(e) => setApplicationFormId(e.target.value)}
                  onFocus={handleFocus}
                  className="form-control form-control-lg"
                  placeholder="Enter Application Form ID"
                />
                <div className="error-message1">{formErrors.application_form_id}</div>
              </div>
              <div className="form-outline mt-3">
                <label>Draft Number</label>
                <input
                  name="draft_no"
                  type="text"
                  value={draftNo}
                  onChange={(e) => setDraftNo(e.target.value)}
                  onFocus={handleFocus}
                  className="form-control form-control-lg"
                  placeholder="Enter Draft Number"
                />
                <div className="error-message1">{formErrors.draft_no}</div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Draft Date</label>
                <input
                  name="draft_date"
                  type="date"
                  value={draftDate}
                  onChange={(e) => setDraftDate(e.target.value)}
                  onFocus={handleFocus}
                  className="form-control form-control-lg"
                />
                <div className="error-message">{formErrors.draft_date}</div>
              </div>
              <div className="form-outline mt-3">
                <label>Amount</label>
                <input
                  name="amount"
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  onFocus={handleFocus}
                  className="form-control form-control-lg"
                  placeholder="Enter Amount"
                />
                <div className="error-message">{formErrors.amount}</div>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowUpdateDraftModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={handleOkUpdateDraftModal}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Draft Dialog */}
      <Dialog
        open={showDeleteDraftModal}
        onClose={() => setShowDeleteDraftModal(false)}
        PaperProps={{
          sx: {
            width: "450px",
            borderRadius: "20px",
            opacity: 1,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle className="dialog-delete">Delete Application Draft</DialogTitle>
        <DialogContent className="content-delete">
          Are you sure you want to delete this application draft?
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowDeleteDraftModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={deleteDraft}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showCancelDraftModal}
        onClose={() => setShowCancelDraftModal(false)}
        PaperProps={{
          sx: {
            width: "450px",
            borderRadius: "20px",
            opacity: 1,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle className="dialog-delete">Cancel Application Draft</DialogTitle>
        <DialogContent className="content-delete">
          Are you sure you want to cancel this application draft?
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowCancelDraftModal(false)}
          >
            No
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={() => {
              updateDraftStatus(cancelDraftId);
              setShowCancelDraftModal(false);
            }}
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>

    </DashboardLayout>


  );
};

export default ApplicationDrafts;